import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, ExternalLink, Search, Crown, Star, Upload, Eye, Ticket, Crown as CrownIcon } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { getEventImage } from '@/lib/eventHelpers';

export default function AdminEventi() {
  const [items, setItems] = useState([]);
  const [zones, setZones] = useState([]);
  const [locali, setLocali] = useState([]);
  const [q, setQ] = useState('');
  const [stato, setStato] = useState('all');
  const [zona, setZona] = useState('all');

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (stato !== 'all') params.stato = stato;
    if (zona !== 'all') params.zona = zona;
    const r = await api.get('/admin/eventi', { params });
    setItems(r.data || []);
  };

  useEffect(() => {
    api.get('/admin/zones').then((r) => setZones(r.data?.zones || []));
    api.get('/admin/locali').then((r) => setLocali(r.data || []));
  }, []);
  useEffect(() => { load(); }, [q, stato, zona]);

  const localeMap = Object.fromEntries(locali.map(l => [l.id, l]));

  const remove = async (e) => {
    if (!window.confirm(`Eliminare "${e.titolo}"?`)) return;
    await api.delete(`/admin/eventi/${e.id}`);
    toast.success('Eliminato'); load();
  };

  return (
    <AdminLayout title="Eventi">
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-subtle)]"/>
          <Input data-testid="admin-eventi-search" placeholder="Cerca per titolo…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-[color:var(--surface)] border-[color:var(--border-c)]" />
        </div>
        <Select value={stato} onValueChange={setStato}>
          <SelectTrigger data-testid="admin-eventi-filter-stato" className="sm:w-44 bg-[color:var(--surface)] border-[color:var(--border-c)]"><SelectValue placeholder="Stato"/></SelectTrigger>
          <SelectContent className="bg-[color:var(--surface)] border-[color:var(--border-c)]">
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="In Revisione">In Revisione</SelectItem>
            <SelectItem value="Pubblicato">Pubblicato</SelectItem>
          </SelectContent>
        </Select>
        <Select value={zona} onValueChange={setZona}>
          <SelectTrigger data-testid="admin-eventi-filter-zona" className="sm:w-40 bg-[color:var(--surface)] border-[color:var(--border-c)]"><SelectValue placeholder="Zona"/></SelectTrigger>
          <SelectContent className="bg-[color:var(--surface)] border-[color:var(--border-c)]">
            <SelectItem value="all">Tutte le zone</SelectItem>
            {zones.map(z => <SelectItem key={z} value={z}>{z}</SelectItem>)}
          </SelectContent>
        </Select>
        <Link to="/admin/eventi/importa"><Button data-testid="admin-eventi-import-button" variant="outline" className="border-[color:var(--gold)] text-[color:var(--gold)] hover:bg-[rgba(214,185,140,0.08)]"><Upload className="w-4 h-4 mr-1"/>Importa CSV</Button></Link>
        <Link to="/admin/eventi/nuovo"><Button data-testid="admin-eventi-new-button" className="bg-[color:var(--gold-strong)] hover:bg-[#C6A21A] text-black"><Plus className="w-4 h-4 mr-1"/>Nuovo Evento</Button></Link>
      </div>

      <div className="rounded-xl border border-[color:var(--border-c)] bg-[color:var(--surface)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-[color:var(--text-subtle)] bg-[color:var(--bg-2)]">
            <tr><th className="p-3">Evento</th><th className="p-3">Data</th><th className="p-3">Locale</th><th className="p-3">Zona</th><th className="p-3">Stato</th><th className="p-3">Click</th><th className="p-3 text-right">Azioni</th></tr>
          </thead>
          <tbody className="divide-y divide-[color:var(--border-c)]">
            {items.length === 0 ? (
              <tr><td colSpan="7" className="p-6 text-center text-[color:var(--text-muted)]">Nessun evento</td></tr>
            ) : items.map((e) => {
              const imgSrc = getEventImage(e, localeMap[e.locale_id]);
              const isPast = (() => { try { return new Date(e.data + 'T23:59:59') < new Date(new Date().toDateString()); } catch { return false; } })();
              return (
              <tr key={e.id} className={`hover:bg-[rgba(214,185,140,0.04)] ${isPast ? 'opacity-60' : ''}`} data-testid="admin-evento-row">
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    {imgSrc ? <img src={imgSrc} alt="" className="w-10 h-10 rounded-md object-cover"/> : <div className="w-10 h-10 rounded-md bg-[color:var(--surface-2)]"/>}
                    <div className="min-w-0">
                      <div className="font-medium truncate max-w-[260px]">{e.titolo}{isPast && <span className="ml-2 text-[10px] text-[color:var(--text-subtle)]">(passato)</span>}</div>
                      <div className="flex gap-1 mt-1">
                        {e.in_evidenza && <Badge className="bg-[color:var(--gold-strong)] text-black h-5"><Star className="w-3 h-3 mr-0.5"/>TOP</Badge>}
                        {e.tavolo_prive && <Badge variant="outline" className="border-[color:var(--gold)] text-[color:var(--gold)] h-5"><Crown className="w-3 h-3 mr-0.5"/>Privé</Badge>}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="p-3 whitespace-nowrap text-[color:var(--text-muted)]">{e.data} • {e.orario}</td>
                <td className="p-3 text-[color:var(--text-muted)]">{localeMap[e.locale_id]?.nome || '-'}</td>
                <td className="p-3 text-[color:var(--text-muted)]">{e.zona}</td>
                <td className="p-3">{e.stato_revisione === 'Pubblicato' ? <Badge className="bg-emerald-700/30 text-emerald-300 hover:bg-emerald-700/30">Pubblicato</Badge> : <Badge className="bg-amber-700/30 text-amber-300 hover:bg-amber-700/30">In Revisione</Badge>}</td>
                <td className="p-3" data-testid="evento-click-counters">
                  <div className="flex flex-col gap-0.5 text-xs">
                    <span className="flex items-center gap-1 text-[color:var(--text-muted)]" title="Visualizzazioni"><Eye className="w-3 h-3 text-[color:var(--gold)]"/>{e.view_count || 0}</span>
                    <span className="flex items-center gap-1 text-[color:var(--text-muted)]" title="Click Acquista"><Ticket className="w-3 h-3 text-emerald-400"/>{e.click_buy_count || 0}</span>
                    <span className="flex items-center gap-1 text-[color:var(--text-muted)]" title="Click Tavolo"><CrownIcon className="w-3 h-3 text-amber-400"/>{e.click_table_count || 0}</span>
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-2">
                    {e.stato_revisione === 'Pubblicato' && <Link to={`/eventi/${e.slug}`} target="_blank" className="text-[color:var(--text-subtle)] hover:text-[color:var(--gold)]"><ExternalLink className="w-4 h-4"/></Link>}
                    <Link to={`/admin/eventi/${e.id}/modifica`}><Button size="sm" variant="ghost" data-testid="admin-evento-edit"><Pencil className="w-4 h-4"/></Button></Link>
                    <Button size="sm" variant="ghost" onClick={() => remove(e)} data-testid="admin-evento-delete" className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4"/></Button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
